import apiRequest from '../../api/request';

// What the Warning Dashboard asked the server, remembered for the session - so coming BACK to the dashboard (browser
// Back, "Back to Warning Dashboard") shows the previous screen at once instead of throwing everything away and
// calling the same 26 endpoints again behind a row of spinners.
//
//   age < FRESH_MS         -> used as it is, NO request at all (clicking back and forth quickly costs nothing)
//   FRESH_MS .. KEEP_MS    -> shown at once, refreshed quietly in the background; the numbers change in place, there
//                             is never a spinner over data the viewer is already looking at
//   older than KEEP_MS     -> treated as unknown: loaded normally, with the spinner
//
// Only successful answers are kept (a failure never replaces good data and is never served), requests for the same
// URL that overlap share ONE call, and every entry belongs to the logged-in user + their session token, so one
// person's numbers are never shown to another after a switch of account.

export const FRESH_MS = 30 * 1000;
export const KEEP_MS = 30 * 60 * 1000;
const MAX_ENTRIES = 400;

const store = new Map(); // key -> { data, fetchedAt }
const inflight = new Map(); // key -> Promise<response>

// Off under Jest unless a test switches it on, so the older tests (one request per mount) stay valid.
let enabled = !(typeof process !== 'undefined' && process.env && process.env.JEST_WORKER_ID);
export const __setDashboardCacheEnabled = (on) => {
  enabled = on;
  store.clear();
  inflight.clear();
};

const viewerKey = () => {
  try {
    const cred = JSON.parse(localStorage.getItem('cred'));
    return `${cred?.profile?.userId ?? ''}:${String(cred?.token || '').slice(-12)}`;
  } catch {
    return 'anonymous';
  }
};
const keyOf = (url) => `${viewerKey()}|${url}`;

/** What is known about this URL right now: { data, fetchedAt, fresh } or null. Synchronous. */
export const readCache = (url, freshMs = FRESH_MS) => {
  if (!enabled) return null;
  const entry = store.get(keyOf(url));
  if (!entry) return null;
  const age = Date.now() - entry.fetchedAt;
  if (age > KEEP_MS) return null;
  return { data: entry.data, fetchedAt: entry.fetchedAt, fresh: age < freshMs };
};

/** The oldest answer among these URLs (to say when the screen was last updated), or null if any is unknown. */
export const oldestFetchedAt = (urls) => {
  let oldest = null;
  for (const url of urls) {
    const entry = readCache(url);
    if (!entry) return null;
    oldest = oldest === null ? entry.fetchedAt : Math.min(oldest, entry.fetchedAt);
  }
  return oldest;
};

/**
 * GET this URL through apiRequest. Overlapping calls for the same URL share one request; a successful answer is
 * remembered. Resolves with the response whatever happened (apiRequest never rejects on an HTTP error) - the caller
 * checks `res.status`. It always asks the server (unless one identical call is already running); deciding whether a
 * remembered answer is good enough is the caller's job (readCache / useDashboardRequest).
 */
export const loadShared = (url) => {
  const key = keyOf(url);
  if (inflight.has(key)) return inflight.get(key);
  const request = Promise.resolve(apiRequest({ url, method: 'get' }))
    .then((res) => {
      if (enabled && res?.status === 'success') {
        store.set(key, { data: res.data, fetchedAt: Date.now() });
        if (store.size > MAX_ENTRIES) store.delete(store.keys().next().value); // oldest inserted goes first
      }
      return res;
    })
    .finally(() => inflight.delete(key));
  inflight.set(key, request);
  return request;
};

export const clearDashboardCache = () => {
  store.clear();
  inflight.clear();
};

// A threshold (or slab, or expense) was saved or switched: everything remembered was worked out with the old settings
// - forget it all, so the next screen asks the server (which also recomputes, see warningDashboardCache.middleware.js).
// Without this, going straight to the results after saving could show the previous numbers for up to 30 seconds.
if (typeof window !== 'undefined') window.addEventListener('warning-dashboard-config-changed', clearDashboardCache);
